import _ from "lodash";
import React, { useCallback } from "react";
import Switch from "react-switch";
import { useIntl } from "react-intl";
import { FormGroup } from "reactstrap";
import { COLOR_PRIMARY } from "../../../constants/theme";
import { useAnalyticsCore } from "../../../cores/analytics";
import { useFacebookCore } from "../../../cores/facebook";
import { useInstagramCore } from "../../../cores/instagram";
import { useMeCore } from "../../../cores/me";
import { useTiktokCore } from "../../../cores/tiktok";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  buildIndividualReportProviders,
  canShowSummaryReport,
} from "./reportProviders";

interface Props {
  summarySelected: boolean;
  onSummaryToggle: (value: boolean) => void;
}

export const SocialReportSelector = ({
  summarySelected,
  onSummaryToggle,
}: Props) => {
  const intl = useIntl();
  const { socialSettings } = useMeCore();
  const { settings: facebookSettings } = useFacebookCore();
  const { settings: instagramSettings } = useInstagramCore();
  const { settings: tiktokSettings } = useTiktokCore();
  const { socialProviders, toggleSocialProvider } = useAnalyticsCore();

  const platformSources = {
    socialSettings,
    facebookSettings,
    instagramSettings,
    tiktokSettings,
  };
  const individualProviders = buildIndividualReportProviders(platformSources);
  const showSummary = canShowSummaryReport(platformSources);

  const isSelected = useCallback(
    (id: string): boolean =>
      Boolean(socialProviders.filter((sp) => sp.id === id).length),
    [socialProviders]
  );

  if (!showSummary && individualProviders.length === 0) {
    return (
      <span className="d-flex h-100 justify-content-center align-items-center">
        {intl.formatMessage({ id: "app.reports.notSocialPost" })}
      </span>
    );
  }

  return (
    <div>
      {showSummary ? (
        <FormGroup className="d-flex align-items-center mb-4">
          <Switch
            checked={summarySelected}
            checkedIcon={false}
            uncheckedIcon={false}
            onColor={COLOR_PRIMARY}
            height={20}
            width={30}
            className="mr-2"
            onChange={onSummaryToggle}
          />
          <FontAwesomeIcon
            icon={["fas", "clipboard-list"]}
            className="text-secondary mr-2"
          />
          <span className="text-secondary font-weight-bold">
            {intl.formatMessage({ id: "app.reports.summary" })}
          </span>
        </FormGroup>
      ) : null}

      {individualProviders.map((sp) => (
        <FormGroup className="d-flex align-items-center mb-4" key={sp.id}>
          <Switch
            checked={isSelected(sp.id)}
            checkedIcon={false}
            uncheckedIcon={false}
            onColor={sp.color}
            height={20}
            width={30}
            className="mr-2"
            onChange={() => toggleSocialProvider(sp)}
          />
          {_.isFunction(sp.icon) ? sp.icon({ active: true, size: "1x" }) : null}
          <span className="text-secondary font-weight-bold ml-2">{sp.name}</span>
        </FormGroup>
      ))}
    </div>
  );
};
